import { country_filter_id } from "@/components/outreach/panel/constants";
import { isPanelCountryBlocked } from "@/components/outreach/panel/countries/blockedPanelCountries";
import {
  COUNTRY_ID_TO_ISO3,
  COUNTRY_ID_TO_NAME,
} from "@/components/outreach/panel/prolific/constants";
import {
  RESPONDENT_COUNTRY_CHOICES,
  RESPONDENT_ISO2_TO_ISO3,
} from "@/components/outreach/panel/respondent/constants";
import { RESPONDENT_FILTER_IDS } from "@/components/outreach/panel/respondent/filter_ids";
import type {
  BaseSelectFilterSelected,
  FilterId,
  PanelType,
  TargetGroup,
} from "@/components/outreach/panel/types";
import {
  USER_INTERVIEWS_COUNTRY_CODE_TO_NAME,
  USER_INTERVIEWS_ISO2_TO_ISO3,
} from "@/components/outreach/panel/user_interviews/constants";
import type { CountryIso3 } from "@/components/outreach/recruitment/panelPrediction/panelRegistry/countries";
import { assertNever } from "@/packages/ts-utils";
import { notNull, isNullish } from "@/utils/helpers";

export const getCountryFilterId = ({
  panel,
}: {
  panel: PanelType;
}): FilterId | null => {
  switch (panel) {
    case "prolific":
    case "user_interviews":
      return country_filter_id;
    case "respondent_b2c":
    case "respondent_b2b":
      return RESPONDENT_FILTER_IDS.country;
    case "custom":
    case "panel_api": // TODO(soon): the reserved country filter in the panel's catalog
      return null;
    default:
      return assertNever(panel);
  }
};

export const isCountryFilterId = ({
  filterId,
}: {
  filterId: FilterId;
}): boolean =>
  filterId === country_filter_id || filterId === RESPONDENT_FILTER_IDS.country;

export const getCountryIso3ByPanelCode = ({
  panel,
}: {
  panel: PanelType;
}): Readonly<Record<string, CountryIso3>> | null => {
  switch (panel) {
    case "prolific":
      return COUNTRY_ID_TO_ISO3;
    case "user_interviews":
      return USER_INTERVIEWS_ISO2_TO_ISO3;
    case "respondent_b2c":
    case "respondent_b2b":
      return RESPONDENT_ISO2_TO_ISO3;
    case "custom":
    case "panel_api": // TODO(soon): the reserved country filter in the panel's catalog
      return null;
    default:
      return assertNever(panel);
  }
};

const getCountryNameByPanelCode = ({
  panel,
}: {
  panel: PanelType;
}): Readonly<Record<string, string>> => {
  switch (panel) {
    case "prolific":
      return COUNTRY_ID_TO_NAME;
    case "user_interviews":
      return USER_INTERVIEWS_COUNTRY_CODE_TO_NAME;
    case "respondent_b2c":
    case "respondent_b2b":
      return RESPONDENT_COUNTRY_CHOICES;
    case "custom":
    case "panel_api": // TODO(soon): the reserved country filter in the panel's catalog
      return {};
    default:
      return assertNever(panel);
  }
};

const isPanelCodeBlocked = ({
  panel,
  panelCode,
}: {
  panel: PanelType;
  panelCode: string;
}): boolean => {
  const countryIso3 = getCountryIso3ByPanelCode({ panel })?.[panelCode];

  return notNull(countryIso3) && isPanelCountryBlocked({ countryIso3 });
};

export const getSupportedCountryNameByPanelCode = ({
  panel,
}: {
  panel: PanelType;
}): Readonly<Record<string, string>> =>
  Object.fromEntries(
    Object.entries(getCountryNameByPanelCode({ panel })).filter(
      ([panelCode, _name]) => !isPanelCodeBlocked({ panel, panelCode })
    )
  );

// Picker options for a country filter: drops blocked countries unless the group already selects them.
// Blocking is applied here, not to the panel's `choices`, because `choices` also resolves labels and
// ids for saved selections (picker tags, the target group summary, targeting errors). Removing a
// blocked country from `choices` would leave existing groups with unlabeled codes they cannot unpick.
// A selected blocked country stays visible so its group shows it and can remove it; once removed it
// is no longer offered. Saves and launches still reject it through `getCountryTargetingError`.
export const getSelectableCountryChoices = ({
  panel,
  choices,
  selectedValues,
}: {
  panel: PanelType;
  choices: Record<string, string>;
  selectedValues: string[];
}): Record<string, string> =>
  Object.fromEntries(
    Object.entries(choices).filter(
      ([panelCode, _name]) =>
        !isPanelCodeBlocked({ panel, panelCode }) ||
        selectedValues.includes(panelCode)
    )
  );

/** Panel-native codes for `countriesIso3`, limited to those the panel supports. */
export const getSupportedCountryPanelCodesFromIso3 = ({
  panel,
  countriesIso3,
}: {
  panel: PanelType;
  countriesIso3: CountryIso3[];
}): string[] => {
  const iso3ByPanelCode = getCountryIso3ByPanelCode({ panel }) ?? {};
  return Object.keys(getSupportedCountryNameByPanelCode({ panel })).filter(
    (panelCode) => countriesIso3.includes(iso3ByPanelCode[panelCode])
  );
};

export const getTargetGroupCountryFilter = ({
  panel,
  filters,
}: Pick<TargetGroup, "panel" | "filters">): BaseSelectFilterSelected | null => {
  const filterId = getCountryFilterId({ panel });
  const countryFilter = filters.find((f) => f.filter_id === filterId);
  if (isNullish(countryFilter)) {
    return null;
  }

  if (countryFilter.type !== "select") {
    throw new Error(
      `Country filter ${countryFilter.filter_id} is ${countryFilter.type}, expected select`
    );
  }

  return countryFilter;
};

/** The group's selected country codes as stored, unvalidated (Prolific digits, ISO-2 elsewhere); [] without a country filter. */
export const getTargetGroupCountryPanelCodes = ({
  panel,
  filters,
}: Pick<TargetGroup, "panel" | "filters">): string[] =>
  getTargetGroupCountryFilter({ panel, filters })?.selected_values ?? [];

export const getTargetGroupCountryIso3 = ({
  panel,
  filters,
}: Pick<TargetGroup, "panel" | "filters">): CountryIso3[] => {
  const iso3ByPanelCode = getCountryIso3ByPanelCode({ panel });
  if (isNullish(iso3ByPanelCode)) {
    return [];
  }

  const countries = getTargetGroupCountryPanelCodes({ panel, filters })
    .map((panelCode) =>
      Object.hasOwn(iso3ByPanelCode, panelCode)
        ? iso3ByPanelCode[panelCode]
        : null
    )
    .filter(notNull);

  return countries;
};
